import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, ClipboardList, Truck, Ticket, Shield, Menu, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getCasingBottomBarItems } from '../config/casingNav';
import { navPathOpenForRole } from '../lib/sales-access';
import { phoneNavIcon, useVerticalPhoneNav } from './verticalPhoneNav';
import { BOTTOM_NAV_Z } from '../lib/layers';

// Maps lucide icon names (strings from casingNav.ts) to their components.
const ICON: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Home,
  ClipboardList,
  Truck,
  Ticket,
  Shield,
};

type BarItem = { path: string; icon: React.ComponentType<{ size?: number; className?: string }>; label: string; vertical: string | null };

/**
 * TRUCKING AND RENTALS TABS COME FROM THE SIDEBAR'S OWN CONFIG (#803): the
 * sidebar's FAVORITES, up to four, then More, which lists the rest. The split
 * lives in `verticalPhoneNav.tsx`, shared with MoreScreen.
 *
 * A CASING TENANT'S BAR IS UNTOUCHED, byte for byte, including a casing tenant
 * that also holds trucking or rentals: the `else` branch below is the code as it
 * was. On 2026-10-01 no casing tenant held trucking or rentals (Smith also holds
 * midstream, which has no tabs here).
 */

/**
 * Bar labels. The sidebar's labels ("Dispatch Board", "Surface Rentals") do not
 * fit a fifth of a phone, so a few have a short form here. A missing entry uses
 * the sidebar's label as it is.
 */
const SHORT_LABEL: Record<string, string> = {
  rentals: 'Rentals',
  'rentals-dispatch': 'Board',
  'trucking-bols': 'BOLs',
  downhole: 'Tools',
  'ndt-technicians': 'Techs',
};

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { hasVertical, user } = useAuth();

  const isCasing = hasVertical('casing_running');
  const verticalNav = useVerticalPhoneNav(hasVertical);
  const usesVerticalNav = verticalNav.enabled;

  let navItems: BarItem[];

  if (usesVerticalNav) {
    navItems = [
      ...verticalNav.bar.map((it) => ({ path: it.path, icon: phoneNavIcon(it.icon), label: SHORT_LABEL[it.id] ?? it.label, vertical: null })),
      { path: '/more', icon: Menu, label: 'More', vertical: null },
    ];
  } else {
    // Casing tabs (Home, Jobs, Dispatch, Tickets, Safety) come from the single-source
    // nav config. When casing is enabled they replace the shared Home/Safety framing.
    const casingTabs = isCasing
      ? getCasingBottomBarItems().map((i) => ({
          path: i.path,
          icon: ICON[i.icon],
          label: i.label,
          vertical: 'casing_running',
        }))
      : [];

    navItems = [
      // Shared Home — only when casing isn't providing its own from the config.
      ...(isCasing ? [] : [{ path: '/', icon: Home, label: 'Home', vertical: null }]),

      // Casing vertical (from config)
      ...casingTabs,

      // Trucking vertical
      ...(hasVertical('trucking') ? [
        // No "Trucking" tab: /trucking is retired and redirects to `/` (fix/trucking-nav).
        { path: '/trucking/dispatch', icon: ClipboardList, label: 'Dispatch', vertical: 'trucking' },
        { path: '/trucking/bols', icon: Ticket, label: 'BOLs', vertical: 'trucking' },
      ] : []),

      // Rentals vertical
      ...(hasVertical('surface_rentals') ? [
        { path: '/rentals', icon: Package, label: 'Rentals', vertical: 'surface_rentals' },
        { path: '/rentals/dispatch', icon: Truck, label: 'Dispatch', vertical: 'surface_rentals' },
      ] : []),

      // Shared Safety — only when casing isn't providing its own from the config.
      ...(isCasing ? [] : [{ path: '/safety', icon: Shield, label: 'Safety', vertical: null }]),
      { path: '/more', icon: Menu, label: 'More', vertical: null },
    ];
  }

  // A sales user sees only tabs whose route they may open (lib/sales-access.ts,
  // which keeps /more). Every other role keeps every tab.
  navItems = navItems.filter((i) => navPathOpenForRole(user?.role, i.path));

  const matches = (path: string) => currentPath === path || currentPath.startsWith(path + '/');
  // On the vertical bar, Overview (/trucking) is a prefix of Dispatch (/trucking/dispatch),
  // and both lit up. There, only the longest matching tab is active. The casing branch keeps
  // its own rule unchanged.
  const longest = usesVerticalNav
    ? navItems.filter((i) => matches(i.path)).reduce<string | null>((best, i) => (best == null || i.path.length > best.length ? i.path : best), null)
    : null;

  return (
    <nav className="md:hidden" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: BOTTOM_NAV_Z, backgroundColor: 'var(--fieldiq-card, #243044)' }}>
      <div className="border-t border-fieldiq-border flex justify-around items-center" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = usesVerticalNav ? item.path === longest : matches(item.path);

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={22} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
    </nav>
  );
}
